/**
 * Validaciones para tipos de identificación según Lucida
 */

/**
 * Valida una cédula física costarricense
 * @param {string} cedula - Número de cédula
 * @returns {boolean}
 */
export const validateCedulaFisica = (cedula) => {
    if (!cedula) return false;

    // Remover espacios y guiones
    const cleanCedula = cedula.replace(/[\s-]/g, '');

    // Debe tener exactamente 9 dígitos
    if (!/^\d{9}$/.test(cleanCedula)) {
        return false;
    }

    // No debe empezar con cero
    if (cleanCedula.startsWith('0')) {
        return false;
    }

    return true;
};

/**
 * Valida una cédula jurídica costarricense
 * @param {string} cedula - Número de cédula jurídica
 * @returns {boolean}
 */
export const validateCedulaJuridica = (cedula) => {
    if (!cedula) return false;

    // Remover espacios y guiones
    const cleanCedula = cedula.replace(/[\s-]/g, '');

    // Debe tener exactamente 10 dígitos
    if (!/^\d{10}$/.test(cleanCedula)) {
        return false;
    }

    // No debe empezar con cero
    if (cleanCedula.startsWith('0')) {
        return false;
    }

    return true;
};

/**
 * Valida un DIMEX
 * @param {string} dimex - Número DIMEX
 * @returns {boolean}
 */
export const validateDimex = (dimex) => {
    if (!dimex) return false;

    // Remover espacios y guiones
    const cleanDimex = dimex.replace(/[\s-]/g, '');

    // Debe tener 11 o 12 dígitos
    if (!/^\d{11,12}$/.test(cleanDimex)) {
        return false;
    }

    // No debe empezar con cero
    if (cleanDimex.startsWith('0')) {
        return false;
    }

    return true;
};

/**
 * Valida un NITE
 * @param {string} nite - Número NITE
 * @returns {boolean}
 */
export const validateNite = (nite) => {
    if (!nite) return false;

    // Remover espacios y guiones
    const cleanNite = nite.replace(/[\s-]/g, '');

    // Debe tener exactamente 10 dígitos
    if (!/^\d{10}$/.test(cleanNite)) {
        return false;
    }

    // No debe empezar con cero
    if (cleanNite.startsWith('0')) {
        return false;
    }

    return true;
};

/**
 * Valida identificación extranjera
 * @param {string} identification - Número de identificación
 * @returns {boolean}
 */
export const validateExtranjera = (identification) => {
    if (!identification) return false;

    // Máximo 20 caracteres
    if (identification.length > 20) {
        return false;
    }

    // Debe tener al menos 3 caracteres
    if (identification.length < 3) {
        return false;
    }

    return true;
};

/**
 * Valida una identificación según su tipo
 * @param {string} identification - Número de identificación
 * @param {number} type - Tipo de identificación (1=Física, 2=Jurídica, 3=DIMEX, 4=NITE, 9=Extranjera)
 * @returns {{isValid: boolean, message: string}}
 */
export const validateIdentification = (identification, type) => {
    if (!identification) {
        return {
            isValid: false,
            message: "La identificación es requerida"
        };
    }

    switch (type) {
        case 1: // Cédula Física
            if (!validateCedulaFisica(identification)) {
                return {
                    isValid: false,
                    message: "Identificación debe tener 9 dígitos, sin cero al inicio ni guiones"
                };
            }
            break;

        case 2: // Cédula Jurídica
            if (!validateCedulaJuridica(identification)) {
                return {
                    isValid: false,
                    message: "Identificación debe tener 10 dígitos, sin cero al inicio ni guiones"
                };
            }
            break;

        case 3: // DIMEX
            if (!validateDimex(identification)) {
                return {
                    isValid: false,
                    message: "Identificación debe tener 11 o 12 dígitos, sin cero al inicio ni guiones. Esta es una cédula de identificación de migración y extranjera."
                };
            }
            break;

        case 4: // NITE
            if (!validateNite(identification)) {
                return {
                    isValid: false,
                    message: "Identificación debe tener 10 dígitos, sin cero al inicio ni guiones. Número de identificación tributario especial que se otorga a obligados tributarios no domiciliados en el país."
                };
            }
            break;

        case 9: // Extranjera
            if (!validateExtranjera(identification)) {
                return {
                    isValid: false,
                    message: "Usar este tipo de identificación si no entra dentro de los criterios anteriores. Máximo 20 caracteres."
                };
            }
            break;

        default:
            return {
                isValid: false,
                message: "Tipo de identificación no válido"
            };
    }

    return {
        isValid: true,
        message: ""
    };
};

/**
 * Formatea la identificación según su tipo
 * @param {string} identification - Número de identificación
 * @param {number} type - Tipo de identificación
 * @returns {string}
 */
export const formatIdentification = (identification, type) => {
    if (!identification) return "";

    // Para todos los tipos, remover espacios y guiones y devolver solo números
    let clean = identification.replace(/[\s-]/g, '');

    // Devolver sin formato especial (sin guiones)
    return clean;
};
